import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Gift, Target, Goal, CheckCircle2, XCircle, History } from "lucide-react";
import { api } from "@/lib/api";
import { RegolamentoModal } from "@/components/RegolamentoModal";

const SEASON = "2026-27";
const TABS = [
  { game: "tiket", label: "Ticket", type: "exact_score" },
  { game: "survival", label: "Survival", type: "exact_score" },
  { game: "score", label: "Score", type: "first_scorer" },
  { game: "fanta", label: "Fanta", type: "first_scorer" },
];

export default function Bonus() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = TABS.some((t) => t.game === searchParams.get("tab"))
    ? searchParams.get("tab") : "survival";
  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState({}); // subId -> {home,away} or {player}
  const [tiketBonus, setTiketBonus] = useState([]); // per-room Ticket bonuses

  const active = TABS.find((t) => t.game === tab);

  const load = useCallback(async () => {
    setLoading(true); setData(null); setLocked(null); setHistory([]); setTiketBonus([]);
    try {
      if (tab === "tiket") {
        const tb = await api("/tiket-bonus").catch(() => []);
        // Ordine dalla giornata più recente alla prima (la corrente per prima):
        // per matchday se presente, altrimenti per scadenza decrescente.
        tb.sort((a, b) => {
          const ma = Number(a.matchday), mb = Number(b.matchday);
          if (!Number.isNaN(ma) && !Number.isNaN(mb) && ma !== mb) return mb - ma;
          return String(b.deadline_at || "").localeCompare(String(a.deadline_at || ""));
        });
        setTiketBonus(tb);
      } else {
        setData(await api(`/bonus/available?game=${tab}&season=${SEASON}`));
        setLocked(await api(`/bonus/current-locked-picks?game=${tab}&season=${SEASON}`).catch(() => null));
        setHistory(await api(`/bonus/history/full?game=${tab}&season=${SEASON}`).catch(() => []));
      }
    }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const submitTiket = async (b) => {
    const v = inputs[`tk-${b.room_id}`] || {};
    const home = v.home != null && v.home !== "" ? v.home : b.my_pick?.home;
    const away = v.away != null && v.away !== "" ? v.away : b.my_pick?.away;
    if (home == null || away == null || home === "" || away === "") { toast.error("Inserisci il risultato esatto"); return; }
    try {
      await api(`/rooms/${b.room_id}/bonus/pick`, { method: "POST", body: { home: Number(home), away: Number(away) } });
      toast.success("Pronostico bonus salvato!");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const submit = async (sub) => {
    const v = inputs[sub.id] || {};
    try {
      if (active.type === "exact_score") {
        await api("/bonus/picks/exact", { method: "POST", body: { game: tab, season: SEASON, subscription_id: sub.id, home_score: Number(v.home || 0), away_score: Number(v.away || 0) } });
      } else {
        if (!v.player) throw new Error("Inserisci il nome del marcatore");
        await api("/bonus/picks/scorer", { method: "POST", body: { game: tab, season: SEASON, subscription_id: sub.id, player_name: v.player } });
      }
      toast.success("Pronostico bonus salvato!");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const setIn = (id, patch) => setInputs((p) => ({ ...p, [id]: { ...p[id], ...patch } }));

  const fmtPick = (pick) => {
    if (pick == null) return "—";
    if (typeof pick === "string" || typeof pick === "number") return String(pick);
    if (pick.home_score != null && pick.away_score != null) return `${pick.home_score}-${pick.away_score}`;
    if (pick.player_name) return pick.player_name;
    if (pick.pick) return fmtPick(pick.pick);
    return JSON.stringify(pick);
  };
  const fmtResult = (r) => {
    if (r == null) return null;
    if (typeof r === "string") return r;
    if (r.home_score != null && r.away_score != null) return `${r.home_score}-${r.away_score}`;
    if (r.player_name) return r.player_name;
    return JSON.stringify(r);
  };

  const cfg = data?.config;
  const subs = data?.subscriptions || [];

  return (
    <div className="space-y-5">
      <button data-testid="bonus-back" onClick={() => navigate("/")} className="flex items-center gap-1 text-[#94A3B8] hover:text-white text-sm transition-colors"><ChevronLeft size={16} /> Hub</button>
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Gift className="text-[#10B981]" /> Giochi Bonus</h1>
          <p className="text-[#94A3B8] text-sm">Un bonus a giornata per ogni gioco: vinci vite o punti extra.</p>
        </div>
        <RegolamentoModal game="bonus" />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button key={t.game} data-testid={`bonus-tab-${t.game}`} onClick={() => setTab(t.game)}
            className={`shrink-0 px-4 py-2 rounded-md text-sm font-bold transition-colors ${tab === t.game ? "bg-[#10B981] text-[#08110A]" : "bg-[#181D22] border border-white/10 text-[#94A3B8]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-[#94A3B8]">Caricamento...</div>
      ) : tab === "tiket" ? (
        tiketBonus.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-[#181D22] p-8 text-center text-[#94A3B8]">Nessun bonus Big Match attivo nelle tue stanze Ticket. L'admin lo attiva nella pagina della stanza.</div>
        ) : (
          <div className="space-y-4">
            {tiketBonus.map((b) => (
              <div key={b.room_id} data-testid={`tk-bonus-${b.room_id}`} className="rounded-xl border border-[#10B981]/30 bg-[#181D22] p-4 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#10B981] flex items-center gap-1"><Target size={13} /> Big Match — risultato esatto</div>
                    <div className="text-lg font-extrabold mt-0.5">{b.home} - {b.away}</div>
                    <div className="text-xs text-[#94A3B8]">Stanza: <b className="text-white">{b.room_name}</b>{b.description ? ` · ${b.description}` : ""}</div>
                  </div>
                  {b.result && <div className="text-sm font-bold text-[#F59E0B]">Risultato: {b.result.home}-{b.result.away}</div>}
                </div>

                {b.locked ? (
                  <div className="space-y-1.5">
                    {b.my_pick && (
                      <div className="text-xs flex items-center gap-1">
                        Il tuo pronostico: <b className="text-[#F59E0B]">{b.my_pick.home}-{b.my_pick.away}</b>
                        {b.my_correct === true && <CheckCircle2 size={14} className="text-[#00D95F]" />}
                        {b.my_correct === false && <XCircle size={14} className="text-[#EF4444]" />}
                      </div>
                    )}
                    <div className="rounded-lg bg-[#0F1216] border border-white/10 divide-y divide-white/5">
                      {(b.picks || []).length === 0 && <div className="px-3 py-2 text-xs text-[#64748B]">Nessuna giocata.</div>}
                      {(b.picks || []).map((p, i) => (
                        <div key={i} data-testid={`tk-bonus-pick-${b.room_id}-${i}`} className="px-3 py-2 flex items-center gap-3 text-sm">
                          <span className="flex-1 min-w-0 truncate font-medium">{p.nickname}</span>
                          <span className="text-[#F59E0B] font-bold">{p.home}-{p.away}</span>
                          {b.result && (p.correct ? <CheckCircle2 size={16} className="text-[#00D95F]" /> : <XCircle size={16} className="text-[#EF4444]" />)}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    {b.my_pick && <div className="text-xs text-[#00D95F] mb-1">Pronostico attuale: {b.my_pick.home}-{b.my_pick.away} (modificabile fino alla scadenza)</div>}
                    <div className="flex items-center gap-2">
                      <input data-testid={`tk-bonus-home-${b.room_id}`} type="number" min="0" placeholder="Casa" defaultValue={b.my_pick?.home ?? ""} onChange={(e) => setIn(`tk-${b.room_id}`, { home: e.target.value })} className="w-20 bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-center" />
                      <span className="text-[#94A3B8]">-</span>
                      <input data-testid={`tk-bonus-away-${b.room_id}`} type="number" min="0" placeholder="Ospite" defaultValue={b.my_pick?.away ?? ""} onChange={(e) => setIn(`tk-${b.room_id}`, { away: e.target.value })} className="w-20 bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-center" />
                      <button data-testid={`tk-bonus-save-${b.room_id}`} onClick={() => submitTiket(b)} className="ml-auto bg-[#10B981] text-[#08110A] font-bold text-sm rounded-md px-4 py-2">Salva</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : !data?.eligible ? (
        <div className="rounded-xl border border-white/10 bg-[#181D22] p-8 text-center text-[#94A3B8]">Non sei iscritto a nessuna stanza/torneo di {active.label}: iscriviti per giocare il bonus.</div>
      ) : !cfg ? (
        <div className="rounded-xl border border-white/10 bg-[#181D22] p-8 text-center text-[#94A3B8]">Nessun bonus attivo al momento per {active.label}.</div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-[#10B981]/40 bg-[#10B981]/5 p-4">
            <div className="text-xs uppercase tracking-widest text-[#10B981] flex items-center gap-1">
              {active.type === "exact_score" ? <Target size={13} /> : <Goal size={13} />}
              {active.type === "exact_score" ? "Big Match — risultato esatto" : "Primo marcatore"}
            </div>
            {cfg.big_match && <div className="text-lg font-extrabold mt-1">{cfg.big_match.home_team} - {cfg.big_match.away_team}</div>}
            <div className="text-sm text-[#94A3B8]">Giornata {cfg.matchday}</div>
          </div>

          {subs.map((s) => (
            <div key={s.id} data-testid={`bonus-sub-${s.id}`} className="rounded-xl border border-white/10 bg-[#181D22] p-4 space-y-2">
              <div className="text-sm font-bold">{s.name || s.room_name || s.tournament_name || "La tua iscrizione"}</div>
              {s.my_pick && <div className="text-xs text-[#00D95F]">Pronostico attuale: {fmtPick(s.my_pick.pick || s.my_pick)}</div>}
              {active.type === "exact_score" ? (
                <div className="flex items-center gap-2">
                  <input data-testid={`bonus-home-${s.id}`} type="number" min="0" placeholder="Casa" onChange={(e) => setIn(s.id, { home: e.target.value })} className="w-20 bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-center" />
                  <span className="text-[#94A3B8]">-</span>
                  <input data-testid={`bonus-away-${s.id}`} type="number" min="0" placeholder="Ospite" onChange={(e) => setIn(s.id, { away: e.target.value })} className="w-20 bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-center" />
                  <button data-testid={`bonus-save-${s.id}`} onClick={() => submit(s)} className="ml-auto bg-[#10B981] text-[#08110A] font-bold text-sm rounded-md px-4 py-2">Salva</button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input data-testid={`bonus-player-${s.id}`} placeholder="Nome marcatore" onChange={(e) => setIn(s.id, { player: e.target.value })} className="flex-1 bg-[#0F1216] border border-white/15 rounded-md px-3 py-2" />
                  <button data-testid={`bonus-save-${s.id}`} onClick={() => submit(s)} className="bg-[#10B981] text-[#08110A] font-bold text-sm rounded-md px-4 py-2">Salva</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pronostici della giornata corrente (visibili dopo la deadline) */}
      {locked?.visible && (locked.picks || []).length > 0 && (
        <div className="space-y-2">
          <h2 className="text-lg font-extrabold flex items-center gap-2"><Target size={18} className="text-[#F59E0B]" /> Pronostici Giornata {locked.matchday}{locked.settled ? " · Liquidato" : ""}</h2>
          {locked.big_match && <div className="text-sm text-[#94A3B8]">{locked.big_match.home_team} - {locked.big_match.away_team}{fmtResult(locked.result) ? ` · Risultato: ${fmtResult(locked.result)}` : ""}</div>}
          <div className="rounded-xl border border-white/10 bg-[#181D22] divide-y divide-white/10">
            {locked.picks.map((p, i) => (
              <div key={i} data-testid={`bonus-locked-pick-${i}`} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                <span className="flex-1 min-w-0 truncate"><b>{p.nickname}</b> <span className="text-[#64748B]">· {p.subscription_name}</span></span>
                <span className="text-[#F59E0B] font-bold">{fmtPick(p.pick)}</span>
                {locked.settled && (p.is_correct ? <CheckCircle2 size={16} className="text-[#00D95F]" /> : <XCircle size={16} className="text-[#EF4444]" />)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Storico bonus: SOLO fuori dal tab Ticket (in Ticket confondeva).
          Giornate ordinate dalla più recente alla prima (4,3,2,1). */}
      {tab !== "tiket" && history.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-extrabold flex items-center gap-2"><History size={18} className="text-[#94A3B8]" /> Storico bonus</h2>
          {[...history].sort((a, b) => (Number(b.matchday) || 0) - (Number(a.matchday) || 0)).map((h, hi) => {
            const winners = (h.picks || []).filter((p) => p.is_correct).length;
            return (
              <div key={hi} data-testid={`bonus-history-${h.matchday}`} className="rounded-xl border border-white/10 bg-[#181D22] p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-extrabold">Giornata {h.matchday}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#00D95F]/15 text-[#00D95F]">{winners} vincitori</span>
                </div>
                {h.big_match && <div className="text-sm text-[#94A3B8]">{h.big_match.home_team} - {h.big_match.away_team}{fmtResult(h.result) ? ` · Risultato: ${fmtResult(h.result)}` : ""}</div>}
                <div className="rounded-lg bg-[#0F1216] border border-white/10 divide-y divide-white/5">
                  {(h.picks || []).length === 0 && <div className="px-3 py-2 text-xs text-[#64748B]">Nessuna giocata.</div>}
                  {(h.picks || []).map((p, pi) => (
                    <div key={pi} data-testid={`bonus-history-pick-${h.matchday}-${pi}`} className="px-3 py-2 flex items-center gap-3 text-sm">
                      <span className="flex-1 min-w-0 truncate"><b>{p.nickname}</b> <span className="text-[#64748B]">· {p.subscription_name}</span></span>
                      <span className="text-[#F59E0B] font-bold">{fmtPick(p.pick)}</span>
                      {p.is_correct ? <CheckCircle2 size={16} className="text-[#00D95F]" /> : <XCircle size={16} className="text-[#EF4444]" />}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
